import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import useBroker from '@hooks/useBroker'
import WebService from '@util/webService'
import { mergeWallets, totalsOf, disagreesOnHalt, POLL_MILLIS, TICK_MILLIS } from '@util/wallet'

/**
 * 지갑 화면의 데이터 한 벌 -- **DB 목록 + 실시간 스냅샷 + 해제 요청**을 여기서 합친다.
 *
 * == useBroker와 갈리는 지점 ==
 *
 * `useBroker`는 범용이다 -- topic/service/idField만 알고 무엇을 구독하든 똑같이 돈다.
 * 그래서 손대지 않고 **안에서 쓴다.** 지갑에만 있는 규칙(DB가 정본, 조건부 폴링, 해제 요청)은
 * 전부 여기 있다.
 *
 * == 왜 페이지에 두지 않나 ==
 *
 * useEffect가 다섯이다(최초 조회 / 신선도 tick / 조건부 폴링 / 불일치 재조회 / mqtt).
 * 페이지에 두면 렌더 코드와 엉키고, 두 번째 화면이 생기는 순간 통째로 복사된다.
 *
 * == targetUserId를 여기서 확정한다 ==
 *
 * DB 조회와 mqtt 구독이 **같은 유저**를 봐야 한다. 지금은 그 유도(`'' -> account.user.id`)가
 * useBroker 안에만 있어서, DB 호출을 따로 쓰면 두 축이 갈릴 여지가 생긴다. 한 군데서 정하고
 * 양쪽에 같은 값을 넘긴다.
 *
 *   userId 없음  -> 본인 지갑   (/user/wallet)
 *   userId 있음  -> 그 유저 지갑 (/user/wallet/:userId, admin)
 *
 * 서버가 같은 규칙으로 다시 확인한다 -- BrokerController는 targetUserId가 다르면 isAdmin을
 * 보고, WalletController.listByUserId도 같다. 화면이 넘기는 값은 편의지 권한이 아니다.
 */

/** **컴포넌트 밖**에 둔다 -- 안에 인라인 배열로 두면 매 렌더 새 참조가 되어 useBroker가
 * 접속을 다시 맺을 여지가 생긴다(그쪽이 topicKeys로 방어하긴 하지만 안 만드는 게 낫다). */
/** 🔴 [2단화, 2026-08-27] **키가 `stateId`("지갑/심볼")다 -- `walletId`가 아니다.**
 *
 * 한 지갑이 심볼 다섯을 돌면 트레이더의 OM 도 다섯이고, 다섯이 **같은 `walletId`로** 각자
 * 한 장씩 발행한다. `walletId`를 키로 쓰면 뒤에 온 장이 앞의 것을 통째로 덮어써 **화면에
 * 심볼 하나만 남았다** -- 나머지 넷은 포지션이 있어도 없는 것처럼 보였다.
 *
 * 신원은 트레이더가 만들어 싣는다(`OrderManager.publishState`의 `stateId`). 화면이
 * `${walletId}/${symbolId}`로 조립하면 조립 규칙이 두 곳에 생기고 한쪽만 바뀌면 조용히
 * 안 맞는다 -- `SignalTable.tableId`가 같은 근거로 그렇게 한다. */
const SUBSCRIPTIONS = [
  { topic: 'wallet', service: 'trader.wallet', idField: 'stateId' },
]

const useWallets = (userId) => {
  const account = useSelector((state) => state.accountReducer)
  const targetUserId = (userId === undefined || userId === null || userId === '')
    ? account?.user?.id
    : userId

  const { records, services, connected } = useBroker(SUBSCRIPTIONS, String(targetUserId ?? ''))

  const [rows, set_rows] = useState([])
  /** 자리 목록 -- `wallet_symbols` 행들. **정지 축이 여기 있다**(30차에 `wallets`에서 내려왔다).
   * 지갑 목록과 같은 호출 흐름에 얹어 두 축의 시점을 맞춘다. */
  const [symbolRows, set_symbolRows] = useState([])
  /** 마스터 계좌 한 줄(`{ account, balance }`). 계좌가 없으면 `account`가 null이고, 계좌는
   * 있는데 관측이 아직이면 `balance`가 null이다 -- 셋을 가르는 근거는 `MaController`. */
  /** 마스터 **목록**(거래소마다 하나). 옛 이름은 `master` 단수였고, 그 이름이
   * "마스터는 하나" 전제를 화면 끝까지 실어 날랐다 -- 서버가 BIN 만 물어보던 것과 짝이다. */
  const [masters, set_masters] = useState([])
  const [loading, set_loading] = useState(true)
  const [error, set_error] = useState('')
  const [busyId, set_busyId] = useState(null)
  const [tick, set_tick] = useState(0)

  /**
   * status 허용값 -- **서버에서 받아온다.** 화면에 상수로 박으면 정본이 셋이 된다
   * (RunStatuses / LiveRunner 조립 검사 / 여기).
   *
   * [2단 게이트, 2026-08-27] 지갑과 자리가 **같은 값 공간**을 쓴다 -- 목록도 하나다.
   *
   * **빈 배열로 시작하고 실패해도 빈 채로 둔다.** 화면은 목록이 비면 드롭다운을 안 그린다 --
   * 못 받았을 때 기본값으로 대신 채우면 서버가 허용값을 줄였는데 화면이 옛 값을 계속
   * 내미는 상황이 생기고, 그 값은 서버가 거부하므로 사람은 "왜 저장이 안 되지"를 본다.
   * 못 받으면 조작을 안 내주는 쪽이 맞다.
   */
  const [statuses, set_statuses] = useState([])

  /** 마지막 DB 조회 시각. 불일치 재조회가 매 tick마다 터지는 걸 막는 유일한 장치다. */
  const lastFetchAt = useRef(0)

  const fetchRows = useCallback(async () => {
    if (targetUserId === undefined || targetUserId === null) {
      return
    }
    // **호출 직전에 찍는다.** 응답 후에 찍으면 느린 응답 하나가 도는 동안 재조회가 여러 번 뜬다.
    lastFetchAt.current = Date.now()

    const resp = await WebService.get(`/wallet/user/${targetUserId}`)
    if (resp.repCode !== 'ack') {
      console.error('지갑 목록 조회 실패=>', resp.repMessage)
      // **rows는 그대로 둔다.** 조회 실패는 상태가 아니라 사건이다 -- 여기서 비우면 DB가
      // 잠깐 끊긴 것만으로 지갑이 화면에서 사라지고, 그건 미기동과 구별되지 않는다.
      set_error(resp.repMessage || '지갑 목록을 불러오지 못했습니다')
      set_loading(false)
      return
    }

    const repMessage = JSON.parse(resp.repMessage)
    set_rows(JSON.parse(repMessage.records || '[]'))
    set_error('')
    set_loading(false)

    // 자리 목록 -- **같은 흐름에 얹는다.** halt와 status가 `wallet_symbols`에 있으므로
    // 지갑 목록만으로는 정지 축을 그릴 수 없다. 따로 폴링하면 두 목록의 시점이 어긋나
    // "지갑은 정상인데 자리가 정지"처럼 한쪽만 낡은 그림이 잠깐씩 뜬다.
    //
    // 실패하면 **빈 배열로 두지 않는다** -- 지갑 목록과 같은 근거다(조회 실패는 상태가
    // 아니라 사건이고, 비우면 정지된 자리가 화면에서 사라진다).
    const symbolResp = await WebService.get(`/wallet/symbols/user/${targetUserId}`)
    if (symbolResp.repCode === 'ack') {
      const symbolMessage = JSON.parse(symbolResp.repMessage)
      set_symbolRows(JSON.parse(symbolMessage.records || '[]'))
    }
    else {
      console.error('자리 목록 조회 실패=>', symbolResp.repMessage)
    }

    // 마스터 잔고 -- **같은 호출 흐름에 얹는다.**
    //
    // 총자산은 `서브 지갑 합 + 마스터`라서 두 값이 **같은 시점**이어야 한다. 따로 폴링하면
    // 주기가 어긋나 합계가 잠깐씩 틀린 숫자를 보이고, 그건 사람이 이체를 의심하게 만드는
    // 종류의 잡음이다. 여기 붙이면 목록 폴링(POLL_MILLIS)이 그대로 마스터까지 끌고 간다.
    //
    // **실패해도 조용히 넘어간다.** 마스터는 총자산 한 줄에만 쓰이므로, 못 받았다고 지갑
    // 목록까지 못 보게 할 이유가 없다. `null`로 두면 화면이 "마스터 없음"이 아니라
    // "관측 대기"로 그린다(그 셋을 가르는 건 서버 응답이다 -- `MaController` 참고).
    const masterResp = await WebService.get(`/ma/user/${targetUserId}`)
    if (masterResp.repCode === 'ack') {
      // `masters`가 없으면 빈 배열이다 -- 옛 서버(단수 응답)와 섞여 돌 때 `undefined.map`으로
      // 화면이 통째로 죽는 것을 막는다.
      set_masters(JSON.parse(masterResp.repMessage || '{}').masters || [])
    }
    else {
      console.error('마스터 잔고 조회 실패=>', masterResp.repMessage)
    }
  }, [targetUserId])

  useEffect(() => {
    set_loading(true)
    fetchRows()
  }, [fetchRows])

  // 허용값은 **한 번만** 읽는다 -- 코드 상수라 서버 재배포 전에는 안 바뀐다. 목록 폴링에
  // 얹으면 10초마다 안 바뀌는 값을 다시 받는다.
  useEffect(() => {
    let alive = true
    const load = async () => {
      const resp = await WebService.get('/wallet/status')
      if (!alive) {
        return
      }
      if (resp.repCode !== 'ack') {
        // 화면 전체를 막을 일은 아니다 -- 읽기는 다 되고 status 조작만 안 나온다.
        console.error('status 허용값 조회 실패=>', resp.repMessage)
        return
      }
      const repMessage = JSON.parse(resp.repMessage)
      set_statuses(JSON.parse(repMessage.statuses || '[]'))
    }
    load()
    return () => { alive = false }
  }, [])

  // 신선도를 다시 재는 시계. 소식이 끊기면 리렌더될 이유가 없어 [갱신 멈춤]이 영영 안 뜬다.
  useEffect(() => {
    const timer = setInterval(() => set_tick((n) => n + 1), TICK_MILLIS)
    return () => clearInterval(timer)
  }, [])

  // tick을 deps에 넣는 이유는 그 값을 쓰기 위해서가 아니라 **시간이 흘렀다는 사실**을
  // 반영하기 위해서다. Date.now()는 deps가 될 수 없다.
  const view = useMemo(
    () => mergeWallets(rows, symbolRows, records.wallet, connected, Date.now()),
    [rows, symbolRows, records.wallet, connected, tick]
  )

  /**
   * 판정 대기 중인 지갑이 하나라도 있으면 DB를 다시 읽는다.
   *
   * **스스로 멈추는 게 요점이다.** 판정이 끝나면 수락은 clearHalt로 컬럼이 비고 거부는
   * halt_at이 요청 시각 뒤로 밀린다 -- 양쪽 다 `reqAt > haltAt`이 깨져 대기 지갑이 0이 되고
   * 이 폴링이 선다. 상시 폴링으로 두면 아무도 안 보는 화면이 10초마다 DB를 때린다.
   */
  // [2단화, 2026-08-27] 대기는 **자리마다** 난다 -- halt 가 `wallet_symbols` 로 내려갔다.
  // 지갑 축으로 접으면(`w.halt`) 그런 값이 아예 없어 읽는 순간 터진다.
  const anyPending = view.wallets.some((w) => w.symbols.some((sy) => sy.halt.pending))
  useEffect(() => {
    if (!anyPending) {
      return undefined
    }
    const timer = setInterval(fetchRows, POLL_MILLIS)
    return () => clearInterval(timer)
  }, [anyPending, fetchRows])

  /**
   * 스냅샷이 DB와 다른 말을 하면 DB를 다시 읽는다.
   *
   * 정지가 새로 걸리는 순간은 **스냅샷이 먼저 안다**(5초). 그렇다고 화면이 스냅샷의 halted를
   * 쓰기 시작하면 출처가 둘이 되므로, 스냅샷은 여기서 **재조회 트리거로만** 쓴다 -- 표시는
   * 끝까지 DB 하나에서 나온다.
   */
  useEffect(() => {
    if (!disagreesOnHalt(view)) {
      return
    }
    if (Date.now() - lastFetchAt.current < POLL_MILLIS) {
      return
    }
    fetchRows()
  }, [view, fetchRows])

  /**
   * 해제 요청 -- 화면이 정지를 푸는 게 아니라 "다 정리했다"고 DB에 신고할 뿐이다.
   * 판정은 트레이더가 10초 주기로 한다.
   *
   * **낙관적 갱신을 하지 않는다.** halt_clear_req_at은 서버가 쓴다. 화면이 Date.now()로
   * 미리 채우면 화면의 시각과 DB의 시각이 달라지고, `reqAt > haltAt`(= 대기 중인가)을
   * 화면이 틀리게 판단한다. 성공하면 다시 읽어서 **서버가 적은 값**으로 그린다.
   */
  const requestHaltClear = useCallback(async (walletId, symbolId) => {
    // busyId가 **자리 키다** -- 지갑 id로 두면 한 자리를 누르는 동안 그 지갑의 나머지 자리
    // 버튼까지 같이 잠긴다. 정지는 자리마다 따로 나는 사건이라 잠금도 자리마다다.
    set_busyId(`${walletId}/${symbolId}`)
    try {
      const resp = await WebService.post(`/wallet/clear/halt/${walletId}/${symbolId}`)
      if (resp.repCode !== 'ack') {
        console.error('halt 해제 요청 실패=>', resp.repMessage)
        set_error(resp.repMessage || '해제 요청에 실패했습니다')
        return false
      }
      set_error('')
      await fetchRows()
      return true
    }
    finally {
      set_busyId(null)
    }
  }, [fetchRows])

  /**
   * 가동 상태 변경 -- 해제 요청과 **같은 규약**이다(낙관적 갱신 없음, 성공하면 다시 읽는다).
   *
   * 화면이 값을 미리 바꾸지 않는 게 여기서는 더 중요하다. 드롭다운의 값을 서버 상태에
   * 묶어두면 실패했을 때 **되돌릴 것이 없다** -- 애초에 안 바뀌기 때문이다. 미리 바꿔놓고
   * 실패 시 롤백하는 방식은 롤백 자체가 또 하나의 실패 지점이고, 그 사이 사람은 바뀐 값을
   * 본다. 상태 변경은 청산까지 가는 조작이라 "바뀐 것처럼 보였다"가 제일 위험하다.
   *
   * 확인 절차는 여기 없다 -- 문구가 링크/정지 상태에 따라 갈리므로 화면이 맡는다
   * (`statusChangeIntent`). 이 함수는 확인이 끝난 뒤에만 불린다.
   */
  const updateStatus = useCallback(async (walletId, status) => {
    set_busyId(walletId)
    try {
      const resp = await WebService.post(`/wallet/update/status/${walletId}/${status}`)
      if (resp.repCode !== 'ack') {
        console.error('status 변경 실패=>', resp.repMessage)
        set_error(resp.repMessage || '가동 상태를 바꾸지 못했습니다')
        return false
      }
      set_error('')
      await fetchRows()
      return true
    }
    finally {
      set_busyId(null)
    }
  }, [fetchRows])

  /**
   * 자리 하나의 가동 상태 -- `wallet_symbols.status`.
   *
   * [2단 게이트, 2026-08-27] **이제 실제로 먹는다.** 그전까지 이 창구가 없던 이유는 트레이더가
   * `wallets.status` 만 봐서, 내주면 눌러도 아무 일이 안 일어나는 버튼이 됐을 것이기 때문이다.
   *
   * 규약은 지갑 쪽과 같다 -- **낙관적 갱신 없음, 성공하면 다시 읽는다.** 잠금은 자리 키다.
   */
  const updateSymbolStatus = useCallback(async (walletId, symbolId, status) => {
    set_busyId(`${walletId}/${symbolId}`)
    try {
      const resp = await WebService.post(
        `/wallet/symbol/update/status/${walletId}/${symbolId}/${status}`)
      if (resp.repCode !== 'ack') {
        console.error('자리 status 변경 실패=>', resp.repMessage)
        set_error(resp.repMessage || '자리 가동 상태를 바꾸지 못했습니다')
        return false
      }
      set_error('')
      await fetchRows()
      return true
    }
    finally {
      set_busyId(null)
    }
  }, [fetchRows])

  /** 확정 잔고 합 -- rows(DB)와 마스터로만 만든다. **스냅샷은 안 쓴다**(`totalsOf` 주석). */
  const totals = useMemo(() => totalsOf(rows, masters), [rows, masters])

  return {
    wallets: view.wallets,
    orphans: view.orphans,
    masters,
    totals,
    connected,
    /** 봉투에 실려 온 트레이더 쪽 장애. content와 무관하게 항상 온다. */
    serviceError: services['trader.wallet']?.error || '',
    loading,
    error,
    busyId,
    /** 서버가 준 status 허용값. 비어 있으면 화면이 조작을 안 그린다. */
    statuses,
    requestHaltClear,
    updateStatus,
    updateSymbolStatus,
    refresh: fetchRows,
  }
}

export default useWallets
